import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { supabase } from "../../../lib/supabase";
import { getSystemSetting } from "../../../lib/emailService";

const AssignmentPanel = ({ selectedUpdate, onAssign }) => {
  const [organizations, setOrganizations] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [expHours, setExpHours] = useState("24");
  const [dlLimit, setDlLimit] = useState("0");
  const [assigning, setAssigning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('organizations')
        ?.select('id, razon_social, nit, email, status')
        ?.eq('status', 'ACTIVE')
        ?.order('razon_social');
      if (error) throw error;
      setOrganizations(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
    // Load system defaults
    Promise.all([
      getSystemSetting('signed_url_ttl_hours'),
      getSystemSetting('signed_url_download_limit'),
    ])?.then(([ttl, limit]) => {
      if (ttl) setExpHours(String(ttl));
      if (limit !== null) setDlLimit(String(limit));
    });
  }, [fetchOrgs]);

  const filtered = organizations?.filter((o) => {
    const q = search?.toLowerCase();
    return !q || o?.razon_social?.toLowerCase()?.includes(q) || o?.nit?.includes(q);
  });

  const toggleOrg = (id) => setSelected((prev) =>
    prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]
  );
  const toggleAll = () => setSelected(
    selected?.length === filtered?.length ? [] : filtered?.map((o) => o?.id)
  );

  const handleAssign = async () => {
    setAssigning(true);
    try {
      await onAssign(selected, { expHours, dlLimit });
      setShowConfirm(false);
      setSelected([]);
    } finally {
      setAssigning(false);
    }
  };

  if (!selectedUpdate) {
    return (
      <div className="flex flex-col items-center justify-center h-full rounded-xl" style={{ background: "var(--color-card)", boxShadow: "var(--shadow-md)" }}>
        <Icon name="Package" size={48} color="var(--color-muted-foreground)" />
        <p className="font-caption text-sm mt-3" style={{ color: "var(--color-muted-foreground)" }}>Seleccione una actualización para asignar</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full rounded-xl overflow-hidden" style={{ background: "var(--color-card)", boxShadow: "var(--shadow-md)" }}>
      {/* Header */}
      <div className="px-4 py-3 border-b" style={{ borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Building2" size={16} color="var(--color-primary)" />
          <h2 className="font-heading font-bold text-base" style={{ color: "var(--color-foreground)" }}>Asignar a Organizaciones</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-caption text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--color-muted)", color: "var(--color-muted-foreground)" }}>{selectedUpdate?.name}</span>
          <span className="font-caption text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--color-muted)", color: "var(--color-muted-foreground)" }}>v{selectedUpdate?.version}</span>
        </div>
      </div>

      {/* Config row */}
      <div className="px-4 py-3 border-b grid grid-cols-2 gap-3" style={{ borderColor: "var(--color-border)" }}>
        <div>
          <Input label="TTL URL (horas)" type="number" value={expHours} onChange={(e) => setExpHours(e?.target?.value)} placeholder="24" />
          <p className="text-xs font-caption mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>Desde config. sistema</p>
        </div>
        <div>
          <Input label="Límite descargas" type="number" value={dlLimit} onChange={(e) => setDlLimit(e?.target?.value)} placeholder="0" />
          <p className="text-xs font-caption mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>0 = ilimitado</p>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b" style={{ borderColor: "var(--color-border)" }}>
        <Input type="search" placeholder="Buscar organización por nombre o NIT..." value={search} onChange={(e) => setSearch(e?.target?.value)} />
      </div>

      {/* Org list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: "var(--color-border)" }}>
              <input type="checkbox" checked={filtered?.length > 0 && selected?.length === filtered?.length} onChange={toggleAll} className="w-4 h-4 accent-teal-600 cursor-pointer" />
              <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>{selected?.length} de {filtered?.length} seleccionadas</span>
            </div>
            {filtered?.map((org) => (
              <div key={org?.id} onClick={() => toggleOrg(org?.id)} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-base border-b" style={{ borderColor: "var(--color-border)" }}>
                <input type="checkbox" checked={selected?.includes(org?.id)} onChange={() => toggleOrg(org?.id)} onClick={(e) => e?.stopPropagation()} className="w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0" />
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-heading font-bold text-xs" style={{ background: "var(--color-primary)", color: "var(--color-primary-foreground)" }}>
                  {org?.razon_social?.split(" ")?.map((n) => n?.[0])?.slice(0, 2)?.join("")}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-caption text-sm font-semibold truncate" style={{ color: "var(--color-foreground)" }}>{org?.razon_social}</p>
                  <p className="font-caption text-xs truncate" style={{ color: "var(--color-muted-foreground)" }}>NIT: {org?.nit}</p>
                </div>
              </div>
            ))}
            {filtered?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10">
                <Icon name="Building2" size={32} color="var(--color-muted-foreground)" />
                <p className="font-caption text-sm mt-2" style={{ color: "var(--color-muted-foreground)" }}>No hay organizaciones activas</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t" style={{ borderColor: "var(--color-border)" }}>
        <Button variant="default" fullWidth disabled={selected?.length === 0} onClick={() => setShowConfirm(true)} iconName="Send" iconPosition="left">
          Asignar a {selected?.length > 0 ? `${selected?.length} org.` : "organizaciones"}
        </Button>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="w-full max-w-sm rounded-xl p-6" style={{ background: "var(--color-card)", boxShadow: "var(--shadow-xl)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(15,118,110,0.1)" }}>
                <Icon name="Send" size={20} color="var(--color-primary)" />
              </div>
              <h4 className="font-heading font-bold text-base" style={{ color: "var(--color-foreground)" }}>Confirmar Asignación</h4>
            </div>
            <p className="font-caption text-sm mb-2" style={{ color: "var(--color-foreground)" }}>
              Se asignará <strong>{selectedUpdate?.name} v{selectedUpdate?.version}</strong> a <strong>{selected?.length} organización(es)</strong>.
            </p>
            <p className="font-caption text-xs mb-4" style={{ color: "var(--color-muted-foreground)" }}>
              URL válida por <strong>{expHours} horas</strong> · Límite: <strong>{Number(dlLimit) === 0 ? 'Ilimitado' : `${dlLimit} descarga(s)`}</strong>
            </p>
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowConfirm(false)} disabled={assigning}>Cancelar</Button>
              <Button variant="default" fullWidth onClick={handleAssign} loading={assigning}>Confirmar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentPanel;